'use client'

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useBoot } from '@/app/(app)/context/BootContext'
import { appBridgeHasSessionToken, waitForAppBridge } from '@/app/(app)/lib/appBridgeSession'

const AppBridgeReadyContext = createContext(false)

/**
 * Single embedded App Bridge readiness source: one wait/poll per shop+host transition.
 * Direct mode (shop without host): ready is true without polling.
 */
export function AppBridgeReadyProvider({ children }: { children: ReactNode }) {
  const { bootShop, bootHost, bootReady } = useBoot()

  const [ready, setReady] = useState(() => {
    if (typeof window === 'undefined') return false
    if (!bootShop || !bootHost) return true
    return appBridgeHasSessionToken()
  })

  useEffect(() => {
    if (!bootReady) return

    if (!bootShop || !bootHost) {
      setReady(true)
      return
    }

    if (appBridgeHasSessionToken()) {
      setReady(true)
      return
    }

    setReady(false)
    let cancelled = false
    waitForAppBridge()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch(() => {
        /* keep false — same as prior useAppBridgeReady */
      })

    return () => {
      cancelled = true
    }
  }, [bootReady, bootShop, bootHost])

  const value = useMemo(() => ready, [ready])

  return (
    <AppBridgeReadyContext.Provider value={value}>{children}</AppBridgeReadyContext.Provider>
  )
}

export function useAppBridgeReady(): boolean {
  return useContext(AppBridgeReadyContext)
}
